import React, { useState, useEffect } from 'react';
import { 
  Avatar, 
  Badge, 
  IconButton, 
  Drawer, 
  Typography 
} from '@material-tailwind/react';
import { 
  BellAlertIcon, 
  Bars3Icon, 
  XMarkIcon 
} from '@heroicons/react/24/outline';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux"; 
import ProfileDrawer from '@/pages/delivery/delivery-dashboard/view-profile';
import { getNotifications } from "@/store/slices/notificationSlice"; 
import { getUserProfile } from "@/store/slices/userSlice";
import { getLoginUserData } from "@/store/slices/newDeliverySlice";
import { useTranslation } from 'react-i18next';

export function Navbar() {
  const navigate = useNavigate();

  const { t } = useTranslation("delivery");
  const dispatch = useDispatch(); 

  const [open, setOpen] = React.useState(false);
  const openDrawer = () => setOpen(true);
  const closeDrawer = () => setOpen(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { notifications } = useSelector((state) => state.notification);
  const { profile_data } = useSelector((state) => state.delivery);

  const profileImage = profile_data?.profile_image;
  const defaultAvatar = "/img/icons/user-profile.png";

  useEffect(() => {
    dispatch(getNotifications({ page: 1 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getLoginUserData());
  }, [dispatch]);

  // const notificationCount =
  //   notifications?.pagination?.total ||
  //   notifications?.data?.pagination?.total ||
  //   0;

  const notificationList = 
    notifications?.data?.notifications || 
    notifications?.notifications || 
    [];

  const unreadCount = notificationList.filter(n => n.status === 'unread').length;

  const getLinkStyles = ({ isActive }) => 
    isActive 
      ? "text-[#EAB308] font-semibold text-[15px]" 
      : "text-gray-500 hover:text-gray-700 transition-colors text-[15px] font-normal";

  const NavLinks = () => (
    <>
      <NavLink to="/delivery-partner/new-deliveries" className={getLinkStyles} onClick={closeDrawer}>
        {t("new_deliveries")}
      </NavLink>
      <NavLink to="/delivery-partner/home" className={getLinkStyles} onClick={closeDrawer}>
        {t("dashboard")}
      </NavLink>
    </>
  );

  return (
    <nav className="bg-white sticky top-0 z-50 border-b border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
        
        {/* Left Section */}
        <div className="flex items-center gap-3">
          <IconButton variant="text" size="lg" className="md:hidden" onClick={openDrawer}>
            <Bars3Icon className="h-8 w-8 stroke-2" />
          </IconButton>

          <div 
            className="flex items-center gap-3 cursor-pointer group" 
            onClick={() => navigate('/delivery-partner/home')}
          >
            <div className="h-10 w-10 flex-shrink-0">
              <img 
                src="/img/logos/logo.png" 
                alt="Logo" 
                className="h-full w-full object-contain group-hover:scale-105 transition-transform" 
              />
            </div>
            <span className="text-lg md:text-xl font-medium text-gray-800 tracking-tight whitespace-nowrap">
              {t("corporate")} {t("supplier")}
            </span>
          </div>
        </div>

        {/* Desktop Nav */}
        <div className="flex items-center gap-10">
          {/* INCREASED GAP FROM 8 TO 12 */}
          <div className="hidden md:flex gap-12 items-center">
            <NavLinks />
          </div>

          {/* Right Utilities */}
          <div className="flex items-center gap-2 md:gap-4">
            {unreadCount > 0 ? (
              <Badge 
                content={unreadCount}
                placement="top-end"
                className="bg-red-600 border-2 border-white text-[12px] min-w-[24px] h-[20px] flex items-center justify-center !absolute !top-0 !right-0 translate-x-1/4 -translate-y-1/4"
              >
                <div className="relative">
                  <IconButton 
                    variant="text" 
                    color="gray" 
                    className="rounded-full" 
                    onClick={() => navigate('/delivery-partner/notifications')}
                  >
                    <img 
                      src="/img/icons/Vector.png" 
                      alt="notification" 
                      className="h-6 w-6 md:h-7 md:w-7 object-contain"
                    />
                  </IconButton>
                </div>
              </Badge>
            ) : (
              <IconButton 
                variant="text" 
                color="gray" 
                className="rounded-full" 
                onClick={() => navigate('/delivery-partner/notifications')}
              >
                <img 
                  src="/img/icons/Vector.png" 
                  alt="notification" 
                  className="h-6 w-6 md:h-7 md:w-7 object-contain"
                />
              </IconButton>
            )}

            <div className="pl-4 border-l border-gray-200 ml-1 md:ml-2">
              <Avatar
                src={profileImage ? profileImage : defaultAvatar}
                alt="user-avatar"
                size="sm"
                variant="circular"
                className="border border-gray-200 cursor-pointer object-cover" 
                onClick={() => setIsProfileOpen(true)}
                onError={(e) => {
                  e.target.src = defaultAvatar;
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer open={open} onClose={closeDrawer} className="p-4">
        <div className="mb-6 flex items-center justify-between">
          <Typography variant="h5" color="blue-gray">{t("menu")}</Typography>
          <IconButton variant="text" color="blue-gray" onClick={closeDrawer}>
            <XMarkIcon strokeWidth={2} className="h-5 w-5" />
          </IconButton>
        </div>
        <div className="flex flex-col gap-6">
          <NavLinks />
        </div>
      </Drawer>

      <ProfileDrawer
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />
    </nav>
  );
};

export default Navbar;